// Picker de "imágenes ya generadas" para adjuntar al chat del Asistente.
// Lista las imágenes activas de `imagenes_generadas` de la empresa (bucket
// `generadas`), de más nuevas a más viejas, y permite elegir VARIAS. Al
// aceptar, las devuelve como adjuntos de referencia.
//
// Mismo patrón visual que ProductFullMenu: mobile = bottom sheet (portal),
// desktop = side panel a la derecha.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, Play, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import CachedImg from '../../components/CachedImg';
import { firmarEnLote } from '../../utils/firmarEnLote';
import ProductGridSkeleton from '../../components/ProductGridSkeleton';
import { esNombreDeVideo, nombreDelPoster } from '../../utils/posterDeVideo';

const GENERADAS_BUCKET = 'generadas';

export interface GeneratedPick {
  /** id de `imagenes_generadas` — lo necesita quien pasa la imagen como
   *  referencia al agente (`source: "generada"`), no sólo como adjunto. */
  id: number;
  /** El ARCHIVO (`full/`): la foto, o el mp4 si es un video. */
  storagePath: string;
  /** Lo que se dibuja: la foto, o el PÓSTER (`opt/<nombre>.png`) si es un video. */
  previewUrl: string;
  bucket: string;
  /** Es un video: se adjunta como video, nunca como imagen
   *  (ADR-2026-09-25-un-video-de-creaciones-se-adjunta-como-video). */
  esVideo?: boolean;
  /** Duración en segundos, si la fila la guarda (los del creador sí). */
  duracionSeg?: number;
}

/** ¿Esta fila de `imagenes_generadas` es un video? Por el tipo y, de respaldo, por la
 *  extensión del archivo. Puro. */
export function esFilaDeVideo(fila: { tipo?: string | null; file_name: string }): boolean {
  return fila.tipo === 'video' || fila.tipo === 'video_pro' || esNombreDeVideo(fila.file_name);
}

/**
 * Las últimas creaciones de la empresa (hasta `limite`), más nuevas primero y ya
 * firmadas: lo que dibuja este selector y lo que dibuja la biblioteca del Agente
 * (ADR-2026-09-12-la-biblioteca-de-adjuntos-en-la-pantalla-del-agente). Una sola
 * request de firmas (`firmarEnLote`) en vez de una por imagen.
 */
export async function cargarGeneradasRecientes(
  companyId: string,
  limite = 60,
  opts: { incluirVideos?: boolean } = {},
): Promise<GeneratedPick[]> {
  return (await cargarGeneradasRecientesPagina(companyId, limite, opts)).picks;
}

/**
 * Lo mismo que `cargarGeneradasRecientes`, más si la base DEVOLVIÓ el tope entero
 * (`vinoLlena`). Las filas cuya miniatura no se pudo firmar se descartan; si "¿hay más?"
 * se midiera sobre lo que quedó, una sola fila sin miniatura entre las primeras 60 cortaba
 * el paginado (ADR-2026-09-25-revision-greptile-el-paginado-se-mide-sobre-lo-que-trajo-la-base).
 */
export async function cargarGeneradasRecientesPagina(
  companyId: string,
  limite = 60,
  /**
   * Los VIDEOS sólo donde se pueden usar: adjuntarlos al chat (el agente los analiza o
   * los edita). Los selectores de referencia de IMAGEN no los muestran: un mp4 no es una
   * foto, y mandado como tal rompe el pedido entero
   * (ADR-2026-09-25-un-video-de-creaciones-se-adjunta-como-video).
   */
  opts: { incluirVideos?: boolean } = {},
): Promise<{ picks: GeneratedPick[]; vinoLlena: boolean }> {
  let query = supabase
    .from('imagenes_generadas')
    .select('id, file_name, created_at, tipo, duracion:datos_generacion->>duration')
    .eq('id_company', companyId)
    .eq('active', true)
    .not('file_name', 'is', null);
  // Sin videos, se sacan en la BASE y no sólo después de traer: el `limit` corta la
  // mezcla, y una empresa cuyas últimas 60 creaciones son videos veía el selector de
  // imágenes vacío aunque tuviera fotos más viejas (mismo motivo que `FILTRO_VIDEO_PG`
  // en `utils/filtroContenido.ts`). Es la negación de `esFilaDeVideo`: `tipo` null pasa
  // explícito porque en Postgres `NULL NOT IN (…)` es NULL, no true, y descartaría las
  // filas viejas que no lo guardaron. El filtro del cliente de abajo queda de respaldo.
  if (!opts.incluirVideos) {
    query = query
      .or('tipo.is.null,tipo.not.in.(video,video_pro)')
      .not('file_name', 'ilike', '%.mp4')
      .not('file_name', 'ilike', '%.webm')
      .not('file_name', 'ilike', '%.mov');
  }
  const { data, error } = await query.order('created_at', { ascending: false }).limit(limite);
  if (error || !data) return { picks: [], vinoLlena: false };
  const vinoLlena = data.length >= limite;
  const filas = (
    data as { id: number; file_name: string; tipo?: string | null; duracion?: string | null }[]
  )
    .map((r) => {
      const esVideo = esFilaDeVideo(r);
      const duracion = Number(r.duracion);
      return {
        id: r.id,
        storagePath: `${companyId}/full/${r.file_name}`,
        // La miniatura de un video es su póster; la de una foto, su `opt/` (800 px,
        // la misma que dibujan el historial y la columna de resultados). Antes era
        // `full/`, el original —hasta 8 MB en 4K— para un tile de 170 px
        // (ADR-2026-09-25-el-selector-de-fuentes-carga-miniaturas-y-por-scroll).
        // `full/` queda de respaldo para las filas que no tengan `opt/`: la firma en
        // lote deja afuera lo que no existe, y acá se elige la que salió.
        previewPath: esVideo
          ? `${companyId}/opt/${nombreDelPoster(r.file_name)}`
          : `${companyId}/opt/${r.file_name}`,
        respaldoPath: esVideo ? null : `${companyId}/full/${r.file_name}`,
        esVideo,
        duracionSeg: esVideo && Number.isFinite(duracion) && duracion > 0 ? duracion : undefined,
      };
    })
    .filter((f) => opts.incluirVideos || !f.esVideo);
  const firmas = await firmarEnLote(
    filas.flatMap((f) => [f.previewPath, ...(f.respaldoPath ? [f.respaldoPath] : [])]),
    GENERADAS_BUCKET,
  );
  const picks = filas.flatMap((f) => {
    const previewUrl =
      firmas.get(f.previewPath) ?? (f.respaldoPath ? firmas.get(f.respaldoPath) : undefined);
    return previewUrl
      ? [
          {
            id: f.id,
            storagePath: f.storagePath,
            previewUrl,
            bucket: GENERADAS_BUCKET,
            ...(f.esVideo ? { esVideo: true, ...(f.duracionSeg ? { duracionSeg: f.duracionSeg } : {}) } : {}),
          },
        ]
      : [];
  });
  return { picks, vinoLlena };
}

interface GeneratedRow {
  id: number;
  storagePath: string;
  signedUrl: string;
  esVideo?: boolean;
  duracionSeg?: number;
}

const GeneratedImagesPicker: React.FC<{
  open: boolean;
  companyId: string | null;
  onCancel: () => void;
  onAccept: (picks: GeneratedPick[]) => void;
  /** Mostrar también los videos (sólo donde se adjuntan al chat). */
  incluirVideos?: boolean;
}> = ({ open, companyId, onCancel, onAccept, incluirVideos = false }) => {
  const [animated, setAnimated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<GeneratedRow[]>([]);
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    if (open) requestAnimationFrame(() => setAnimated(true));
    else {
      setAnimated(false);
      setSelected([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    setLoading(true);
    void cargarGeneradasRecientes(companyId, 60, { incluirVideos }).then((picks) => {
      if (cancelled) return;
      setRows(
        picks.map((p) => ({
          id: p.id,
          storagePath: p.storagePath,
          signedUrl: p.previewUrl,
          ...(p.esVideo ? { esVideo: true, duracionSeg: p.duracionSeg } : {}),
        })),
      );
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, companyId, incluirVideos]);

  if (!open) return null;

  const close = (cb: () => void) => {
    setAnimated(false);
    setTimeout(cb, 280);
  };

  const toggle = (id: number) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const accept = () => {
    const byId = new Map(rows.map((r) => [r.id, r]));
    const picks: GeneratedPick[] = selected
      .map((id) => byId.get(id))
      .filter((r): r is GeneratedRow => !!r)
      .map((r) => ({
        id: r.id,
        storagePath: r.storagePath,
        previewUrl: r.signedUrl,
        bucket: GENERADAS_BUCKET,
        ...(r.esVideo ? { esVideo: true, duracionSeg: r.duracionSeg } : {}),
      }));
    close(() => onAccept(picks));
  };

  const grid = (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {rows.map((r) => {
        const isSel = selected.includes(r.id);
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => toggle(r.id)}
            className={`relative aspect-square rounded-[13px] overflow-hidden bg-neutral-100 transition-all active:scale-[0.98] ${
              isSel
                ? 'ring-2 ring-brand-primary'
                : 'ring-1 ring-black/[0.06]'
            }`}
          >
            <CachedImg
              src={r.signedUrl}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
            />
            {r.esVideo && (
              <span className="absolute bottom-2 left-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/55 text-white">
                <Play className="w-3 h-3 fill-white" />
              </span>
            )}
            {isSel && (
              <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow-sm">
                <Check className="w-3.5 h-3.5" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  const body = loading ? (
    <ProductGridSkeleton
      count={6}
      gridClassName="grid grid-cols-2 sm:grid-cols-3 gap-3"
      tileClassName="rounded-[13px]"
    />
  ) : rows.length === 0 ? (
    <div className="text-center text-[14px] text-[#3C3C43]/55 py-16 px-6">
      Todavía no generaste ninguna imagen.
    </div>
  ) : (
    grid
  );

  const acceptLabel = selected.length > 0 ? `Aceptar (${selected.length})` : 'Aceptar';

  return ReactDOM.createPortal(
    <>
      {/* ── Mobile bottom sheet ───────────────────────────────────────── */}
      <div
        className={`md:hidden fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => close(onCancel)}
      />
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-[211] bg-white rounded-t-[13px] flex flex-col transition-transform duration-300 ease-out ${
          animated ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          height: 'calc(100dvh - 1rem - env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        <div className="py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={() => close(onCancel)}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Imágenes generadas
          </h3>
          <button
            type="button"
            onClick={accept}
            disabled={selected.length === 0}
            className="text-[15px] font-semibold text-brand-primary active:opacity-50 transition-opacity w-20 text-right disabled:opacity-30"
          >
            Aceptar
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pt-3">{body}</div>
      </div>

      {/* ── Desktop side panel ────────────────────────────────────────── */}
      <div
        className={`hidden md:block fixed inset-0 z-[210] bg-black/20 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => close(onCancel)}
      />
      <div
        className={`hidden md:flex fixed top-4 right-4 bottom-4 w-[560px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[211] flex-col overflow-hidden transition-transform duration-300 ease-out ${
          animated ? 'translate-x-0' : 'translate-x-[calc(100%+1rem)]'
        }`}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4 shrink-0">
          <h3 className="text-[18px] font-semibold text-brand-black/90">
            Imágenes generadas
          </h3>
          <button
            type="button"
            onClick={() => close(onCancel)}
            aria-label="Cerrar"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-brand-black/70 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-4">{body}</div>
        <div className="px-6 py-4 border-t border-black/[0.06] shrink-0">
          <button
            type="button"
            onClick={accept}
            disabled={selected.length === 0}
            className="w-full py-3 rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-semibold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {acceptLabel}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default GeneratedImagesPicker;
